import { logoutSuccess } from '../actions/session_actions';
import { newestExecutionSuccess } from '../actions/agent_executions_actions';
import FetchUtils, { parseJSON } from './fetch_utils';
import { isCancelledRead, throughLatest } from './agent_service_request';
import { getAgentIntelligenceUri } from '../utils/EnvConfigUtils';
import {
	AOPEventPage,
	AOPExecutionDetail,
	AOPExecutionPage,
	AOPExecutionState,
	AOPOverrideAction,
	AOPEventOrder,
	AOPExecutionOutcome,
	AOPExecution,
	pageRows,
} from '../types/AOPExecutionTypes';
const fetchUtils = new FetchUtils();

/**
 * What Radar has run, against openwrench-intelligence-service#22
 * (`/api/v1/buyer/agent/executions`). Same deployment and same auth as the
 * authoring calls in agent_procedures_thunks — see the note there.
 *
 * Almost nothing here writes to the store. A run's history is a cursor-paged
 * feed read by one screen, so holding it in a reducer would buy nothing and
 * would need a page-merging policy of its own; those thunks resolve the page
 * and the tab keeps it. Overrides are fire-and-refetch for the same reason.
 *
 * The one exception is `readNewestWorkOrderExecution`, which two surfaces on
 * the work-order page ask independently — see its own note.
 */

const JSON_HEADERS = { Accept: 'application/json', 'Content-Type': 'application/json' };

const agentUrl = (suffix: string): string =>
	`${getAgentIntelligenceUri()}/api/v1/buyer/agent${suffix}`;

const errorCodeOf = (body: any, fallback: string): string => {
	const code = body && body.error ? body.error.code : null;
	return typeof code === 'string' ? code : fallback;
};

const queryStringOf = (params: { [key: string]: any }): string => {
	const pairs = Object.keys(params)
		.filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== '')
		.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`);
	return pairs.length > 0 ? `?${pairs.join('&')}` : '';
};

/**
 * What `GET /executions` and `GET /execution_events` can both be asked for.
 *
 * `state` tells a held run from a working one and `outcome` asks how a finished
 * one ended, so a funnel is filtered by the service rather than by reading
 * pages and sorting them here. Every page carries `count` — the number the
 * filters match, not the number on the page.
 */
export interface AOPRunFilters {
	state?: AOPExecutionState;
	outcome?: AOPExecutionOutcome;
	after?: string | null;
	limit?: number;
}

/**
 * One read at a time per feed, the way the table thunks do it.
 *
 * `restCrudThunksGenerator` keeps a `takeLatest` per target collection so that
 * a response to a query the user has moved on from cannot land after the one
 * they are looking at. The same thing happens here: flipping the funnel or the
 * sort order restarts a feed, and a slow answer to the previous query would
 * otherwise replace the events under the controls that no longer asked for it.
 *
 * The key is the caller's, not this module's, because the same thunk serves
 * both a screen's feed and the five small count requests beside it — keyed by
 * what is read, those five would cancel each other and the feed with them. A
 * read with no key is never cancelled.
 */

/**
 * Whether a read was dropped because a newer one replaced it.
 *
 * A superseded read rejects rather than resolving, and that rejection is not a
 * failure: the screen belongs to the query that replaced it, so a caller that
 * sees this should leave the screen alone rather than report an error or clear
 * a spinner the newer read is still using.
 */
export { isCancelledRead };

/** Every read here is the same GET-a-page shape; only the URL differs. */
const readPage =
	<TPage>(url: string, fallbackCode: string, feed?: string) =>
	(dispatch): Promise<TPage> =>
		new Promise((resolve, reject) => {
			throughLatest(feed, fetchUtils.get(url, { headers: JSON_HEADERS }).then(parseJSON))
				.then(({ json, status, ok }) => {
					if (status === 401) {
						dispatch(logoutSuccess());
						reject('unauthorized');
						return;
					}

					if (!ok) {
						reject(errorCodeOf(json, fallbackCode));
						return;
					}

					resolve(json);
				})
				// A cancelled read is passed through as it is: it carries no message,
				// and the caller tells it from a failure by its shape.
				.catch((error) => reject(isCancelledRead(error) ? error : error.message));
		});

/**
 * The runs of one procedure, newest first. `after` is the previous page's
 * `next_cursor`; omitting it reads the first page.
 */
export const readProcedureExecutions =
	(procedureId: string, params: AOPRunFilters = {}, feed?: string) =>
	(dispatch): Promise<AOPExecutionPage> => {
		const query = queryStringOf({ procedure_id: procedureId, ...params });
		return dispatch(
			readPage<AOPExecutionPage>(agentUrl(`/executions${query}`), 'executions_unavailable', feed)
		);
	};

/**
 * The runs on one work order, newest first. A work order can reach a procedure
 * more than once — it can be reopened — so this is a list, not a record.
 */
export const readWorkOrderExecutions =
	(workOrderId: number, params: AOPRunFilters = {}) =>
	(dispatch): Promise<AOPExecutionPage> => {
		const query = queryStringOf({ work_order_id: workOrderId, ...params });
		return dispatch(
			readPage<AOPExecutionPage>(agentUrl(`/executions${query}`), 'executions_unavailable')
		);
	};

/**
 * In-flight reads of "the newest run on this work order", by work order.
 *
 * Two surfaces ask this question about the same work order in the same tick:
 * the Radar banner on the details tab, and `WorkOrdersDetailPage` deciding
 * whether the "Radar Activity" tab exists. A store cache alone does not help
 * there, because neither has landed when the other asks — so the promise is
 * what is shared, and the second caller joins the first one's request.
 *
 * Cleared when it settles, so a later mount reads again rather than being
 * handed an answer from a previous visit.
 */
const newestInFlight: { [workOrderId: string]: Promise<AOPExecution | null> } = {};

/**
 * The newest Radar run on a work order, read once however many ask.
 *
 * Resolves `null` when Radar has never run there, and writes that to the store
 * as well — "asked, nothing" is a different answer from "not asked", and both
 * callers draw differently for each.
 *
 * `force` is for after an override: the banner relays a pause to Radar and
 * then watches for the run to catch up, which has to re-ask rather than be
 * handed the answer from before it asked.
 */
export const readNewestWorkOrderExecution =
	(workOrderId: number, force = false) =>
	(dispatch): Promise<AOPExecution | null> => {
		const key = String(workOrderId);

		if (!force && newestInFlight[key]) {
			return newestInFlight[key];
		}

		const request = dispatch(readWorkOrderExecutions(workOrderId, { limit: 1 }))
			.then((page) => {
				// Newest first from the service, so the first row is the run in play.
				const rows = pageRows<AOPExecution>(page);
				const newest = rows.length > 0 ? rows[0] : null;
				dispatch(newestExecutionSuccess(workOrderId, newest));
				return newest;
			})
			.finally(() => {
				if (newestInFlight[key] === request) {
					delete newestInFlight[key];
				}
			});

		newestInFlight[key] = request;
		return request;
	};

/**
 * One run, with the plan it executed.
 *
 * The list gives a run's state and its version number; this gives the document
 * behind that version as the run started it — so a session can say what Radar
 * was actually following, which nothing can show today. No screen calls it
 * yet: where that belongs is a design question, and this is the plumbing.
 */
export const readExecution =
	(executionId: string) =>
	(dispatch): Promise<AOPExecutionDetail> =>
		dispatch(
			readPage<AOPExecutionDetail>(
				agentUrl(`/executions/${encodeURIComponent(executionId)}`),
				'execution_unavailable'
			)
		);

/**
 * One run's history, oldest first — the order Radar did it in.
 *
 * `/execution_events` is the one event endpoint now: the run, the procedure and
 * the work order are filters on it, so this and the procedure-wide feed below
 * are the same call asked differently. It replaced `/procedures/{id}/events`
 * and `/executions/{id}/events`.
 */
export const readExecutionEvents =
	(
		executionId: string,
		params: { after?: string | null; limit?: number; order?: AOPEventOrder } = {}
	) =>
	(dispatch): Promise<AOPEventPage> => {
		const query = queryStringOf({ execution_id: executionId, ...params });
		return dispatch(
			readPage<AOPEventPage>(agentUrl(`/execution_events${query}`), 'events_unavailable')
		);
	};

/**
 * Everything Radar has done on the procedure, across its runs.
 *
 * `order` is the service's own parameter, so the feed is sorted where it is
 * paged — reversing a cursor page here would only reverse the page, not the
 * feed. Callers pass it rather than leaning on the endpoint's default, which
 * is `asc` and was `desc` on the route this replaced: a default that moves is
 * not something two screens wanting opposite orders should depend on.
 */
export const readProcedureEvents =
	(
		procedureId: string,
		params: {
			after?: string | null;
			limit?: number;
			order?: AOPEventOrder;
			state?: AOPExecutionState;
			outcome?: AOPExecutionOutcome;
			/** One work order's lines out of the procedure's whole feed. */
			work_order_id?: number;
			/** Inclusive bounds on `occurred_at`, as the service names them. */
			occurred_at_start_date?: string;
			occurred_at_end_date?: string;
		} = {},
		feed?: string
	) =>
	(dispatch): Promise<AOPEventPage> => {
		const query = queryStringOf({ procedure_id: procedureId, ...params });
		return dispatch(
			readPage<AOPEventPage>(agentUrl(`/execution_events${query}`), 'events_unavailable', feed)
		);
	};

/**
 * Tells Radar to hold a run, continue it, or end it for good.
 *
 * The service does not store the override: it relays it to Radar and answers
 * 202, and the run's next event is what says whether it took — which is why
 * nothing here writes a state and the caller refetches instead.
 *
 * Two refusals are worth telling apart. `execution_not_running` means the run
 * finished before the click landed, which is the screen being out of date.
 * `radar_unavailable` means the service has no way to reach Radar — it needs an
 * override URL and a shared secret configured, and answers 503 without them.
 */
export const overrideExecution =
	(executionId: string, action: AOPOverrideAction, reason: string | null) =>
	(dispatch): Promise<void> =>
		new Promise((resolve, reject) => {
			fetchUtils
				.post(agentUrl(`/executions/${executionId}/overrides`), {
					headers: JSON_HEADERS,
					data: reason ? { action, reason } : { action },
				})
				.then(parseJSON)
				.then(({ json, status, ok }) => {
					if (status === 401) {
						dispatch(logoutSuccess());
						reject('unauthorized');
						return;
					}

					if (!ok) {
						reject(errorCodeOf(json, 'override_failed'));
						return;
					}

					resolve();
				})
				.catch((error) => reject(error.message));
		});

/** An override refusal, in words. The run itself is unchanged by any of these. */
export const describeOverrideError = (code: string): string => {
	if (code === 'execution_not_running') {
		return 'This run has already finished.';
	}
	if (code === 'radar_unavailable') {
		return 'Radar cannot be reached right now, so the run was left as it is.';
	}
	return 'Could not reach Radar with that.';
};
